import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { stockLedgerApi, warehouseApi, vendorApi, productsApi } from '../services/api';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  Search, Filter, FileText, Download, Plus,
  ArrowDownCircle, ArrowUpCircle, RefreshCw,
  Building2, User, FileCheck2, X, Loader2, AlertTriangle, MapPin
} from 'lucide-react';

const { RangePicker } = DatePicker;

const formatExpiry = (expiryStr) => {
  if (!expiryStr) return '—';
  try {
    const d = new Date(expiryStr);
    if (isNaN(d.getTime())) {
      return expiryStr;
    }
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${month}, ${year}`;
  } catch (e) {
    return expiryStr;
  }
};

const formatQuantity = (val, prefix) => {
  if (val === null || val === undefined || val === '' || val === 0) return '—';
  const num = Math.abs(Number(val));
  if (isNaN(num)) return val;
  return `${prefix}${num.toLocaleString('en-IN')}`;
};

const formatClosingBalance = (val) => {
  const num = Number(val || 0);
  const formatted = Math.abs(num).toLocaleString('en-IN');
  return num >= 0 ? `+${formatted}` : `-${formatted}`;
};


function StockLedger() {
  const { currentTheme } = useTheme();
  const isDark = ['Purple', 'Black'].includes(currentTheme?.name);
  const navigate = useNavigate();

  // States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [showFilters, setShowFilters] = useState(false);

  // Filters Panel States
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  // 🔧 FIXED: stores warehouse ID now (was warehouse NAME before, which never
  // matched anything server-side because the backend expects an integer id).
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterTxType, setFilterTxType] = useState('All');
  const [filterProduct, setFilterProduct] = useState('All');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterVendor, setFilterVendor] = useState('All');
  const [filterQuickRange, setFilterQuickRange] = useState('');

  // ── API DATA STATES ──
  const [ledgerData, setLedgerData] = useState([]);
  const [summary, setSummary] = useState(null);         // totalIn/totalOut/countsByType — full dataset ke
  const [warehousesList, setWarehousesList] = useState([]);
  const [vendorsList, setVendorsList] = useState([]);
  const [productsList, setProductsList] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Debounce — top-bar search + panel search + batch input pe (har keystroke pe API mat maaro)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debouncedFilterSearch, setDebouncedFilterSearch] = useState('');
  const [debouncedBatch, setDebouncedBatch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilterSearch(filterSearch), 400);
    return () => clearTimeout(t);
  }, [filterSearch]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBatch(filterBatch), 400);
    return () => clearTimeout(t);
  }, [filterBatch]);

  // Type Badges styling mapping
  const badgeConfig = {
    'Stock In': { color: 'text-emerald-700 bg-emerald-50 border-emerald-250', dot: 'bg-emerald-500' },
    'Dispatch': { color: 'text-amber-700 bg-amber-50 border-amber-250', dot: 'bg-amber-500' },
    'Transfer In': { color: 'text-sky-705 bg-sky-50 border-sky-250', dot: 'bg-sky-500' },
    'Transfer Out': { color: 'text-purple-700 bg-purple-50 border-purple-250', dot: 'bg-purple-500' },
    'Return': { color: 'text-rose-700 bg-rose-50 border-rose-250', dot: 'bg-rose-500' },
    'Adjustment': { color: 'text-slate-600 bg-slate-50 border-slate-200', dot: 'bg-slate-400' }
  };

  // Backend enum (STOCK_IN / TRANSFER_OUT...) ↔ UI label ('Stock In' / 'Transfer Out'...)
  // ⚠️ 20_Enum_Classes.java se exact values verify karo
  const toEnumType = (label) =>
    label === 'All' ? undefined : label.toUpperCase().replace(/ /g, '_');
  const fromEnumType = (val) => {
    if (!val) return '';
    return String(val)
      .toLowerCase()
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const initials = (name = '') => name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || '—';

  // Backend ledger row → UI shape mapper
  // 🔧 FIXED: date/batch/balance were reading field names (`date`/`createdAt`,
  // `batchNo`, `balance`/`balanceQty`) that don't exist on the real response —
  // the actual fields are `transactionDate`, `batchNumber`, and `balanceAfter`.
  // Those columns rendered blank/0 even once the list itself started loading.
  const mapRow = (r, idx) => {
    const type = fromEnumType(r.transactionType || r.type);
    const qtyIn = r.qtyIn ?? (r.quantityIn > 0 ? r.quantityIn : null);
    const qtyOut = r.qtyOut ?? (r.quantityOut > 0 ? r.quantityOut : null);
    const balance = r.balanceAfter ?? r.balance ?? r.balanceQty ?? 0;

    // Standard business logic fallbacks if individual fields don't exist
    const purchase = r.purchase ?? (type === 'Stock In' || type === 'Purchase' ? qtyIn : null);
    const transferIn = r.transferIn ?? (type === 'Transfer In' ? qtyIn : null);
    const rto = r.rto ?? (type === 'Return' || type === 'RTO' ? qtyIn : null);
    const sale = r.sale ?? (type === 'Dispatch' || type === 'Sale' ? qtyOut : null);
    const transferOut = r.transferOut ?? (type === 'Transfer Out' ? qtyOut : null);
    const freeSample = r.freeSample ?? (type === 'Free Sample' ? qtyOut : null);
    const expiredDamage = r.expiredDamage ?? (type === 'Expired' || type === 'Damage' || type === 'Adjustment' ? qtyOut : null);

    const closingBalance = r.closingBalance ?? balance;
    const openingBalance = r.openingBalance ?? (closingBalance - (qtyIn || 0) + (qtyOut || 0));

    return {
      id: r.id ?? idx,
      date: r.date || (r.transactionDate || r.createdAt ? new Date(r.transactionDate || r.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
      time: r.time || (r.transactionDate || r.createdAt ? new Date(r.transactionDate || r.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''),
      txnId: r.transactionId || r.txnId || `TXN-${r.id}`,
      productName: r.productName || '',
      sku: r.sku || '',
      batchNo: r.batchNumber || r.batchNo || '',
      expiry: formatExpiry(r.expiryDate || r.expiry),
      openingBalance,
      purchase,
      transferIn,
      rto,
      sale,
      transferOut,
      freeSample,
      expiredDamage,
      closingBalance,
      warehouse: r.warehouseName || r.warehouse || '',
      type,
      qtyIn,
      qtyOut,
      balance,
      user: r.createdBy || r.userName || r.user || '',
      initials: initials(r.createdBy || r.userName || r.user),
    };
  };

  // ── Search params builder (list + export dono use karte hain) ──
  // 🔧 FIXED: was sending `warehouse: filterWarehouse` — stockLedgerApi.searchLedger
  // only ever reads `params.warehouseId`, so this key never matched and the
  // warehouse filter silently did nothing. Renamed to `warehouseId`, and the
  // dropdown below now stores the warehouse's numeric id instead of its name.
  const buildParams = () => ({
    searchTerm: debouncedSearch || debouncedFilterSearch,
    warehouseId: filterWarehouse !== 'All' ? filterWarehouse : undefined,
    transactionType: toEnumType(selectedType !== 'All' ? selectedType : filterTxType),
    product: filterProduct,
    batchNumber: debouncedBatch,
    vendor: filterVendor,
    startDate: filterDateFrom,
    endDate: filterDateTo,
    quickRange: filterQuickRange || undefined, // 'Today' | '7d' | '30d' — api.js normalizes casing
  });

  // ── FETCH: Dropdowns (mount pe ek baar) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [w, v, p] = await Promise.all([
          warehouseApi.getActiveWarehouses().catch(() => []),
          vendorApi.getActiveVendors().catch(() => []),
          productsApi.getAllProducts({ page: 0, size: 200 }).catch(() => []),
        ]);
        if (cancelled) return;
        setWarehousesList(Array.isArray(w) ? w : (w?.content || w?.data || []));
        setVendorsList(Array.isArray(v) ? v : (v?.content || v?.data || []));
        setProductsList(Array.isArray(p) ? p : (p?.content || p?.data || []));
      } catch (err) {
        console.error('Dropdown load error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── FETCH: Ledger data + Summary (koi bhi filter change → server-side search) ──
  const loadLedger = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      // ✅ CHANGED: dropped the separate getLedgerSummary() call. The
      // /stock-ledger list response already includes every aggregate we need
      // for THIS filter set — totalInUnits, totalOutUnits, netMovement,
      // totalEntries, and a count per transaction type (stockInCount,
      // dispatchCount, transferInCount, transferOutCount, returnCount,
      // adjustmentCount) — confirmed from an actual response. The separate
      // /stock-ledger/summary endpoint was a guess and was 400ing; no longer
      // needed since this one call already has everything.
      const res = await stockLedgerApi.searchLedger(params);
      // 🔧 FIXED: backend response shape is { entries: [...], adjustmentCount, ... }
      // — not { content: [...] } or { data: [...] }. Same bug pattern as the
      // stock-transfer list (res.transfers) fixed earlier. The old fallback
      // chain landed on the raw object whenever neither `content` nor `data`
      // existed, so Array.isArray(list) was always false and the table stayed
      // empty even though ENTRIES/summary counts (which read a different field)
      // were showing the correct number.
      const list = res?.entries || res?.content || res?.data || res || [];
      setLedgerData(Array.isArray(list) ? list.map(mapRow) : []);

      if (res && typeof res === 'object' && !Array.isArray(res)) {
        setSummary({
          totalIn: res.totalInUnits,
          totalOut: res.totalOutUnits,
          netMovement: res.netMovement,
          totalEntries: res.totalEntries,
          countsByType: {
            STOCK_IN: res.stockInCount,
            DISPATCH: res.dispatchCount,
            TRANSFER_IN: res.transferInCount,
            TRANSFER_OUT: res.transferOutCount,
            RETURN: res.returnCount,
            ADJUSTMENT: res.adjustmentCount,
          },
        });
      } else {
        setSummary(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load ledger');
      console.error('Ledger load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch, debouncedFilterSearch, debouncedBatch,
    selectedType, filterWarehouse, filterTxType, filterProduct,
    filterVendor, filterDateFrom, filterDateTo, filterQuickRange
  ]);

  // ── Export (blob download api.js me handled) ──
  const handleExport = async (format) => {
    try {
      setExporting(true);
      await stockLedgerApi.exportLedger({ format, ...buildParams() });
    } catch (err) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Data ab server-side filtered hai — direct use
  const filteredData = ledgerData;

  // Metrics — summary API se, fallback current page pe compute
  const totalIn = summary?.totalIn ?? filteredData.reduce((sum, row) => sum + (row.qtyIn || 0), 0);
  const totalOut = summary?.totalOut ?? filteredData.reduce((sum, row) => sum + (row.qtyOut || 0), 0);
  const netMovement = summary?.netMovement ?? (totalIn - totalOut);
  const totalEntries = summary?.totalEntries ?? filteredData.length;

  // Type counts — summary.countsByType map se; nahi mila toh current data se
  const getTypeCount = (type) => {
    if (summary?.countsByType) {
      return summary.countsByType[toEnumType(type)] ?? summary.countsByType[type] ?? 0;
    }
    return ledgerData.filter(row => row.type === type).length;
  };

  return (
    <div className="space-y-6 pb-20">
      {/* ── HEADER ── */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Stock Ledger
          </h1>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-550'} mt-1`}>
            {loading ? 'Loading transaction history...' : `Complete transaction history · ${filteredData.length} entries · All warehouses`}
          </p>
        </div>

        {/* Toolbar controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search bar */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search product, TXN ID, user..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 pr-4 py-2 rounded-xl text-xs w-full outline-none border focus:border-emerald-500 transition-all ${
                isDark
                  ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                  : 'bg-white border-gray-200 text-gray-800 placeholder-gray-455'
              }`}
            />
          </div>

          {/* Filter button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-xl border hover:opacity-80 transition-all cursor-pointer ${
              showFilters
                ? 'bg-emerald-50 border-emerald-300 text-emerald-600'
                : isDark
                  ? 'bg-slate-900 border-slate-800 text-gray-400'
                  : 'bg-white border-gray-200 text-gray-500'
            }`}
          >
            <Filter className="w-4 h-4" />
          </button>

          {/* Export PDF */}
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold hover:opacity-85 transition-all cursor-pointer disabled:opacity-50 ${
              isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
            }`}>
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin text-rose-500" /> : <FileText className="w-3.5 h-3.5 text-rose-500" />}
            PDF
          </button>

          {/* Export Excel */}
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold hover:opacity-85 transition-all cursor-pointer disabled:opacity-50 ${
              isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700'
            }`}>
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" /> : <Download className="w-3.5 h-3.5 text-emerald-600" />}
            Export Excel
          </button>

          {/* New Stock In button */}
          <button
            onClick={() => navigate('/stock-management/stock-in')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-all shadow-md cursor-pointer"
            style={{ backgroundColor: currentTheme.color }}
          >
            <Plus className="w-3.5 h-3.5" />
            New Stock In
          </button>
        </div>
      </div>

      {/* ── ERROR BANNER ── */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={loadLedger} className="ml-auto text-xs font-black uppercase tracking-wider underline cursor-pointer">
            Retry
          </button>
        </div>
      )}

      {/* ── COLLAPSIBLE FILTERS PANEL ── */}
      {showFilters && (
        <div className={`p-6 rounded-2xl border transition-all duration-300 relative ${
          isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-gray-200 shadow-sm'
        }`}>
          {/* Close X Button */}
          <button
            onClick={() => setShowFilters(false)}
            className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            title="Close filters"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* Column 1 */}
            <div className="space-y-4">
              {/* Search */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Product, SKU, batch, user..."
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    className={`pl-9 pr-4 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
                    }`}
                  />
                </div>
              </div>

              {/* Warehouse — API se. 🔧 value ab wh.id hai (pehle wh.name tha, jo
                  backend ke integer warehouseId param se kabhi match nahi karta tha). */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Warehouse</label>
                <select
                  value={filterWarehouse}
                  onChange={(e) => setFilterWarehouse(e.target.value)}
                  className={`px-3 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                    isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
                  }`}
                >
                  <option value="All">Please Select</option>
                  {warehousesList.map(wh => (
                    <option key={wh.id} value={wh.id}>{wh.name}</option>
                  ))}
                </select>
              </div>

              {/* Batch Number */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Batch Number</label>
                <input
                  type="text"
                  placeholder="e.g. B240601"
                  value={filterBatch}
                  onChange={(e) => setFilterBatch(e.target.value)}
                  className={`px-3 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                    isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
                  }`}
                />
              </div>
            </div>

            {/* Column 2 */}
            <div className="space-y-4">
              {/* Date Range */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Date Range</label>
                <RangePicker
                  value={
                    filterDateFrom && filterDateTo
                      ? [dayjs(filterDateFrom), dayjs(filterDateTo)]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setFilterDateFrom(dates[0].format('YYYY-MM-DD'));
                      setFilterDateTo(dates[1].format('YYYY-MM-DD'));
                      setFilterQuickRange('');
                    } else {
                      setFilterDateFrom('');
                      setFilterDateTo('');
                    }
                  }}
                  format="DD-MM-YYYY"
                  style={{ width: '100%', padding: '6px 12px', borderRadius: '10px' }}
                  className="w-full text-xs"
                  size="middle"
                />
              </div>

              {/* Transaction Type */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Transaction Type</label>
                <select
                  value={filterTxType}
                  onChange={(e) => setFilterTxType(e.target.value)}
                  className={`px-3 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                    isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
                  }`}
                >
                  <option value="All">Please Select</option>
                  <option value="Stock In">Stock In</option>
                  <option value="Transfer In">Transfer In</option>
                  <option value="Transfer Out">Transfer Out</option>
                  <option value="Dispatch">Dispatch</option>
                  <option value="Return">Return</option>
                  <option value="Adjustment">Adjustment</option>
                </select>
              </div>

              {/* Vendor — API se */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Vendor</label>
                <select
                  value={filterVendor}
                  onChange={(e) => setFilterVendor(e.target.value)}
                  className={`px-3 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                    isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
                  }`}
                >
                  <option value="All">Please Select</option>
                  {vendorsList.map(v => (
                    <option key={v.id} value={v.name || v.companyName}>{v.name || v.companyName}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Column 3 */}
            <div className="space-y-4">

              {/* Product — API se */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Product</label>
                <select
                  value={filterProduct}
                  onChange={(e) => setFilterProduct(e.target.value)}
                  className={`px-3 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                    isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
                  }`}
                >
                  <option value="All">Please Select</option>
                  {productsList.map(p => (
                    <option key={p.id} value={p.productName || p.name}>{p.productName || p.name}</option>
                  ))}
                </select>
              </div>

              {/* Quick Range */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-500">Quick Range</label>
                <div className="flex gap-2.5">
                  {['Today', '7d', '30d'].map(range => (
                    <button
                      key={range}
                      type="button"
                      onClick={() => setFilterQuickRange(filterQuickRange === range ? '' : range)}
                      style={filterQuickRange === range ? { backgroundColor: currentTheme.color, color: '#ffffff', borderColor: currentTheme.color } : {}}
                      className={`flex-1 py-2 border rounded-full text-xs font-bold transition-all cursor-pointer ${
                        filterQuickRange === range
                          ? 'shadow-sm font-black text-white'
                          : isDark
                            ? 'bg-slate-900 border-slate-850 text-slate-400 hover:text-white'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>
            </div>

          </div>

          {/* Reset Filters button */}
          <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-gray-150 dark:border-slate-800/50">
            <button
              onClick={() => {
                setFilterSearch('');
                setFilterDateFrom('');
                setFilterDateTo('');
                setFilterWarehouse('All');
                setFilterTxType('All');
                setFilterProduct('All');
                setFilterBatch('');
                setFilterVendor('All');
                setFilterQuickRange('');
              }}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border cursor-pointer ${
                isDark ? 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Reset Filters
            </button>
            <button
              onClick={() => setShowFilters(false)}
              className="px-4 py-2 rounded-xl text-xs font-bold text-white transition-all shadow-md cursor-pointer"
              style={{ backgroundColor: currentTheme.color }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* ── STATS CARDS ROW (API: ledger/summary) ── */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
        {/* Card 1: Entries */}
        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
          isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
        }`}>
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Entries</p>
            <p className={`text-xl font-black mt-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {loading ? '—' : Number(totalEntries).toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        {/* Card 2: Total In */}
        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
          isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
        }`}>
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
            <ArrowDownCircle className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total In</p>
            <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
              {loading ? '—' : `+${Number(totalIn).toLocaleString('en-IN')} units`}
            </p>
          </div>
        </div>

        {/* Card 3: Total Out */}
        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
          isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
        }`}>
          <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 dark:bg-rose-950/20 dark:text-rose-455 flex items-center justify-center flex-shrink-0">
            <ArrowUpCircle className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Out</p>
            <p className="text-xl font-black text-rose-500 mt-0.5">
              {loading ? '—' : `-${Number(totalOut).toLocaleString('en-IN')} units`}
            </p>
          </div>
        </div>

        {/* Card 4: Net Movement */}
        <div className={`p-5 rounded-2xl border flex items-center gap-4 ${
          isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
        }`}>
          <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 dark:bg-sky-950/20 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
            <RefreshCw className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Net Movement</p>
            <p className={`text-xl font-black mt-0.5 ${netMovement >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500'}`}>
              {loading ? '—' : `${netMovement >= 0 ? '+' : ''}${Number(netMovement).toLocaleString('en-IN')} units`}
            </p>
          </div>
        </div>
      </div>

      {/* ── TYPE FILTERS PILLS ── */}
      <div className="flex flex-wrap items-center gap-2.5 pb-2">
        <button
          onClick={() => setSelectedType('All')}
          style={selectedType === 'All' ? { backgroundColor: currentTheme.color, color: '#ffffff', borderColor: currentTheme.color } : {}}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all cursor-pointer ${
            selectedType === 'All'
              ? 'shadow-sm font-black'
              : isDark
                ? 'bg-slate-955 border-slate-800 text-slate-400 hover:text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          All ({loading ? '…' : totalEntries})
        </button>

        {Object.keys(badgeConfig).map(type => (
          <button
            key={type}
            onClick={() => setSelectedType(selectedType === type ? 'All' : type)}
            style={selectedType === type ? { backgroundColor: currentTheme.color, color: '#ffffff', borderColor: currentTheme.color } : {}}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border transition-all cursor-pointer ${
              selectedType === type
                ? 'shadow-sm font-black'
                : isDark
                  ? 'bg-slate-955 border-slate-800 text-slate-400 hover:text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${selectedType === type ? 'bg-white' : badgeConfig[type].dot}`} />
            {type} ({loading ? '…' : getTypeCount(type)})
          </button>
        ))}
      </div>

      {/* ── TABLE CONTAINER ── */}
      <div className={`border rounded-2xl overflow-hidden transition-all ${
        isDark ? 'bg-slate-900/40 border-slate-800/80' : 'bg-white border-gray-150 shadow-sm'
      }`}>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[1700px]">
            <thead>
              <tr className={`border-b text-[10px] font-black uppercase tracking-wider pb-3 ${
                isDark ? 'border-slate-800 text-[#8AA394]' : 'border-gray-150 text-[#556B5D]'
              }`}>
                <th className="py-4 pl-6 pr-4 whitespace-nowrap">DATE & TIME</th>
                <th className="py-4 px-4 whitespace-nowrap">TRANSACTION ID</th>
                <th className="py-4 px-4 w-72 whitespace-nowrap">PRODUCT</th>
                <th className="py-4 px-4 whitespace-nowrap">EXPIRY</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">OPENING BALANCE</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">PURCHASE</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">TRANSFER IN</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">RTO</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">SALE</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">TRANSFER OUT</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">FREE SAMPLE</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">EXPIRED/DAMAGE</th>
                <th className="py-4 px-4 text-center whitespace-nowrap">CLOSING BALANCE</th>
                <th className="py-4 pl-4 pr-6 whitespace-nowrap">WAREHOUSE</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800/40">
              {loading ? (
                <tr>
                  <td colSpan="14" className="py-14 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mx-auto mb-2" />
                    <span className="text-gray-400 text-sm">Loading ledger entries...</span>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="14" className="py-12 text-center text-gray-400 text-sm font-semibold">
                    No ledger entries found matching the active filters.
                  </td>
                </tr>
              ) : (
                filteredData.map(row => {
                  return (
                    <tr key={row.id} className="hover:bg-slate-50/20 dark:hover:bg-slate-900/5 transition-colors">
                      {/* Date & Time */}
                      <td className="py-4 pl-6 pr-4 whitespace-nowrap">
                        <p className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{row.date}</p>
                        <p className="text-[10px] text-gray-400 font-semibold mt-0.5">{row.time}</p>
                      </td>

                      {/* Transaction ID */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        <span className="text-xs font-mono font-black text-emerald-600 dark:text-emerald-400">
                          {row.txnId}
                        </span>
                      </td>

                      {/* Product Name / SKU / Batch */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        <p className={`text-xs font-bold leading-normal ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {row.productName}
                        </p>
                        <p className="text-[9px] text-gray-400 font-mono mt-0.5">{row.sku}</p>
                        {row.batchNo && (
                          <div className="mt-1">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold border ${
                              isDark 
                                ? 'bg-slate-850 border-slate-700 text-slate-400' 
                                : 'bg-[#FDFBF7] border-gray-200 text-gray-500'
                            }`}>
                              {row.batchNo}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* Expiry */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        <span className={`text-xs font-bold ${isDark ? 'text-slate-300' : 'text-gray-900'}`}>
                          {row.expiry}
                        </span>
                      </td>

                      {/* Opening Balance */}
                      <td className="py-4 px-4 text-center text-xs font-mono font-bold text-emerald-600 whitespace-nowrap">
                        {formatQuantity(row.openingBalance, '+')}
                      </td>

                      {/* Purchase */}
                      <td className="py-4 px-4 text-center text-xs font-mono font-bold text-emerald-600 whitespace-nowrap">
                        {formatQuantity(row.purchase, '+')}
                      </td>

                      {/* Transfer In */}
                      <td className="py-4 px-4 text-center text-xs font-mono font-bold text-emerald-600 whitespace-nowrap">
                        {formatQuantity(row.transferIn, '+')}
                      </td>

                      {/* RTO */}
                      <td className="py-4 px-4 text-center text-xs font-mono font-bold text-emerald-600 whitespace-nowrap">
                        {formatQuantity(row.rto, '+')}
                      </td>

                      {/* Sale */}
                      <td className="py-4 px-4 text-center text-xs font-mono font-bold text-amber-700 whitespace-nowrap">
                        {formatQuantity(row.sale, '-')}
                      </td>

                      {/* Transfer Out */}
                      <td className="py-4 px-4 text-center text-xs font-mono font-bold text-amber-700 whitespace-nowrap">
                        {formatQuantity(row.transferOut, '-')}
                      </td>

                      {/* Free Sample */}
                      <td className="py-4 px-4 text-center text-xs font-mono font-bold text-amber-700 whitespace-nowrap">
                        {formatQuantity(row.freeSample, '-')}
                      </td>

                      {/* Expired/Damage */}
                      <td className="py-4 px-4 text-center text-xs font-mono font-bold text-amber-700 whitespace-nowrap">
                        {formatQuantity(row.expiredDamage, '-')}
                      </td>

                      {/* Closing Balance */}
                      <td className={`py-4 px-4 text-center text-xs font-mono font-bold whitespace-nowrap ${
                        Number(row.closingBalance) >= 0 ? 'text-emerald-600' : 'text-amber-700'
                      }`}>
                        {formatClosingBalance(row.closingBalance)}
                      </td>

                      {/* Warehouse name */}
                      <td className="py-4 pl-4 pr-6 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className={`text-xs font-bold ${isDark ? 'text-slate-300' : 'text-gray-700'} whitespace-nowrap`}>
                            {row.warehouse}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        <div className={`px-6 py-4 flex items-center justify-between border-t text-xs ${
          isDark ? 'bg-slate-900/60 border-slate-800 text-slate-400' : 'bg-gray-50 border-gray-150 text-gray-555'
        }`}>
          <span className="font-semibold">{loading ? 'Loading...' : `${filteredData.length} of ${totalEntries} entries`}</span>
          <span className="text-emerald-600 font-bold">
            Net: {loading ? '—' : `${netMovement >= 0 ? '+' : ''}${Number(netMovement).toLocaleString('en-IN')} units`}
          </span>
        </div>
      </div>
    </div>
  );
}

export default StockLedger;